import React, { useEffect, useState, useMemo } from "react";
import {
  Flex,
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  IconButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  Spinner,
} from "@chakra-ui/react";
import { AddIcon, EditIcon, DeleteIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import {
  getJenisUji,
  addJenisUji,
  updateJenisUji,
  deleteJenisUji,
} from "services/master/jenisUjiService";

export default function MasterJenisUji() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editing, setEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const toast = useToast();
  const textColor = useColorModeValue("gray.700", "white");

  const initialForm = {
    kategori: "",
    parameter_uji: "",
    methode: "",
    requirement: "",
    satuan: "",
  };
  const [form, setForm] = useState(initialForm);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getJenisUji();
      setData(res.data || []);
    } catch (err) {
      toast({
        title: "Gagal memuat data Jenis Uji",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter Search
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    const lower = searchTerm.toLowerCase();
    return data.filter((item) => {
      const param = item.parameter_uji ? item.parameter_uji.toLowerCase() : "";
      const kat = item.kategori ? item.kategori.toLowerCase() : "";
      const meth = item.methode ? item.methode.toLowerCase() : "";
      return param.includes(lower) || kat.includes(lower) || meth.includes(lower);
    });
  }, [data, searchTerm]);

  // Pagination calculation
  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const currentPagedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const openAdd = () => {
    setForm(initialForm);
    setEditing(false);
    setEditingId(null);
    onOpen();
  };

  const openEdit = (row) => {
    setForm({
      kategori: row.kategori || "",
      parameter_uji: row.parameter_uji || "",
      methode: row.methode || "",
      requirement: row.requirement || "",
      satuan: row.satuan || "",
    });
    setEditing(true);
    setEditingId(row.id_uji);
    onOpen();
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing && editingId) {
        await updateJenisUji(editingId, form);
        toast({
          title: "Jenis uji berhasil diubah",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        await addJenisUji(form);
        toast({
          title: "Jenis uji berhasil ditambah",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      }
      onClose();
      fetchData();
    } catch (err) {
      toast({
        title: "Gagal menyimpan",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Yakin ingin menghapus jenis uji ID: ${id}?`)) return;
    try {
      await deleteJenisUji(id);
      toast({
        title: "Berhasil dihapus",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchData();
    } catch (err) {
      toast({
        title: "Gagal menghapus",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <CardHeader p="6px 0px 22px 0px" display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>
          <Text fontSize="xl" color={textColor} fontWeight="bold">
            Master Data Jenis Pengujian
          </Text>
          <HStack spacing={3}>
            <InputGroup size="sm" w={{ base: "180px", md: "260px" }}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Cari parameter / kategori..."
                borderRadius="15px"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Button leftIcon={<AddIcon />} colorScheme="teal" size="sm" borderRadius="10px" onClick={openAdd}>
              Tambah Jenis Uji
            </Button>
          </HStack>
        </CardHeader>
        <CardBody>
          {loading ? (
            <Flex justify="center" p={8}>
              <Spinner size="xl" color="teal.500" />
            </Flex>
          ) : (
            <>
              <Table variant="simple" color={textColor}>
                <Thead>
                  <Tr my=".8rem" color="gray.400">
                    <Th color="gray.400">ID Uji</Th>
                    <Th color="gray.400">Kategori</Th>
                    <Th color="gray.400">Parameter Uji</Th>
                    <Th color="gray.400">Metode</Th>
                    <Th color="gray.400">Requirement</Th>
                    <Th color="gray.400">Satuan</Th>
                    <Th color="gray.400">Aksi</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {currentPagedData.length === 0 ? (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py={6}>
                        {searchTerm ? "Data tidak ditemukan sesuai pencarian" : "Tidak ada data"}
                      </Td>
                    </Tr>
                  ) : (
                    currentPagedData.map((row) => (
                      <Tr key={row.id_uji}>
                        <Td>{row.id_uji}</Td>
                        <Td>{row.kategori || "-"}</Td>
                        <Td fontWeight="bold">{row.parameter_uji}</Td>
                        <Td>{row.methode || "-"}</Td>
                        <Td>{row.requirement || "-"}</Td>
                        <Td>{row.satuan || "-"}</Td>
                        <Td>
                          <IconButton
                            aria-label="edit"
                            icon={<EditIcon />}
                            size="sm"
                            mr={2}
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => openEdit(row)}
                          />
                          <IconButton
                            aria-label="delete"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => handleDelete(row.id_uji)}
                          />
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>

              {/* Pagination Controls */}
              <Flex justify="space-between" align="center" mt={4} flexWrap="wrap" gap={3}>
                <HStack spacing={2} align="center">
                  <Text fontSize="sm" color="gray.500">
                    Tampilkan
                  </Text>
                  <Select
                    size="sm"
                    w="80px"
                    borderRadius="8px"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Select>
                  <Text fontSize="sm" color="gray.500">
                    dari {filteredData.length} data
                  </Text>
                </HStack>

                <HStack spacing={2}>
                  <IconButton
                    aria-label="prev"
                    icon={<ChevronLeftIcon />}
                    size="sm"
                    isDisabled={currentPage <= 1}
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  />
                  <Text fontSize="sm">
                    {currentPage} / {totalPages}
                  </Text>
                  <IconButton
                    aria-label="next"
                    icon={<ChevronRightIcon />}
                    size="sm"
                    isDisabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  />
                </HStack>
              </Flex>
            </>
          )}
        </CardBody>
      </Card>

      {/* Modal Add/Edit */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? "Ubah Jenis Uji" : "Tambah Jenis Uji"}</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <FormControl isRequired mb={3}>
                <FormLabel>Kategori</FormLabel>
                <Input
                  name="kategori"
                  value={form.kategori}
                  onChange={handleChange}
                  placeholder="Contoh: Mikrobiologi, Kimia"
                />
              </FormControl>
              <FormControl isRequired mb={3}>
                <FormLabel>Parameter Uji</FormLabel>
                <Input
                  name="parameter_uji"
                  value={form.parameter_uji}
                  onChange={handleChange}
                  placeholder="Masukkan Parameter Uji"
                />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Metode</FormLabel>
                <Input
                  name="methode"
                  value={form.methode}
                  onChange={handleChange}
                  placeholder="Masukkan Metode Pengujian"
                />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Requirement / Standar</FormLabel>
                <Input
                  name="requirement"
                  value={form.requirement}
                  onChange={handleChange}
                  placeholder="Masukkan Syarat / Batas Standar"
                />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Satuan</FormLabel>
                <Input
                  name="satuan"
                  value={form.satuan}
                  onChange={handleChange}
                  placeholder="Contoh: mg/kg, koloni/g"
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Batal
              </Button>
              <Button type="submit" colorScheme="teal">
                Simpan
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
